import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { ApiResponse, ApiServiceInterface, UserServiceInterface } from '@hutsix/ngxh6';

export interface PermitClosure {
    /** Null means island-wide. */
    location: string | null;
    locationId: string;
    startDate: string;
    endDate: string;
}

/**
 * Upcoming closures, shown above an application form.
 *
 * TLC's flowchart wants closed dates greyed out in the date picker. These forms are
 * generated from the backend schema, so until the picker itself can be constrained
 * this tells the applicant before they choose - the submit-time check is still what
 * actually stops the application.
 *
 * Deliberately no reason given: the reason for a cultural closure is not always
 * TLC's to share, and "pick other dates" is the action either way.
 */
@Component({
    selector: 'app-closure-notice',
    templateUrl: './closure-notice.component.html',
    styleUrls: ['./closure-notice.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ClosureNoticeComponent implements OnInit {
    public closures: PermitClosure[] = [];

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        @Inject('UserService') public userService: UserServiceInterface,
        private cdRef: ChangeDetectorRef,
    ) {}

    /**
     * A signed-in staff member is exempt from the closure block (they are the way a
     * permit gets issued for a closed place), so the warning stays but the "pick other
     * dates" framing does not apply to them.
     *
     * This is read from the template on a public, logged-out page. isGranted can throw
     * before the user service has settled its login state - and a throw here would take
     * the whole closure warning down with it, which is the last thing an applicant
     * looking at closed dates should lose. So it fails closed: any error means "not
     * staff", and the ordinary warning shows.
     */
    public get isStaff(): boolean {
        try {
            return this.userService.isGranted(['ROLE_ADMIN', 'ROLE_STAFF']);
        } catch {
            return false;
        }
    }

    ngOnInit(): void {
        this.api
            // Logged-out applicants have no JWT. skipAuth is what actually makes this
            // fire for them: without it the ApiService waits for a valid or refreshable
            // token and, finding neither, the request hangs and never reaches the server -
            // so the whole closure notice stays blank. bypassOfflineQueue keeps it off the
            // offline queue as well. Both are needed; this is a public endpoint.
            .get({ url: '/api/public/permit/closures', skipAuth: true, bypassOfflineQueue: true, displayErrors: false })
            .subscribe(
                (res: ApiResponse<PermitClosure[]>) => {
                    this.closures = res.data ?? [];
                    this.cdRef.detectChanges();
                },
                () => {
                    // A closure list that won't load must not block an application - the
                    // submit-time check still catches closed dates.
                    this.closures = [];
                    this.cdRef.detectChanges();
                },
            );
    }

    public label(closure: PermitClosure): string {
        return closure.location ?? 'All Tiwi Islands locations';
    }
}
